import {
  AudioPlaybackRate,
  IAudioPlayerClient,
  IAudioSpeechClient,
  ITrack,
} from "./audio-client";
import { ReactivePropertyInstance } from "@agp/shared.common/use-reactive-property";
import { Guide } from "../../entity";
import {
  guideToPlayerTrack,
  guideToSpeechTrack,
} from "../../extension/entity-extension/guide.extension";
import { LocalizedEntityRepository } from "../localized-entity.repository";
import { Language } from "@agp/shared.common/entities/language";
import { PlayerMode } from "./player-mode";
import { ApplicationStateRepository } from "../application-state.repository";
import { LibraryScope } from "./library-scope";
import { ActivityLogModule } from "../activity-log.module";

export class GuidePlayerModule {
  private readonly audioPlayerClient: IAudioPlayerClient;
  private readonly audioSynthesisClient: IAudioSpeechClient;
  private readonly localizedEntityRepository: LocalizedEntityRepository;
  private readonly applicationStateRepository: ApplicationStateRepository;
  private readonly activityLogModule: ActivityLogModule;
  private readonly languages: Language[];
  private readonly libraryScope: LibraryScope;

  private readonly _currentTrack = new ReactivePropertyInstance<ITrack | null>(
    null
  );
  private readonly _playerMode = new ReactivePropertyInstance<PlayerMode>(
    "AudioPlayer"
  );
  private readonly _canNext = new ReactivePropertyInstance<boolean>(false);
  private readonly _canPrev = new ReactivePropertyInstance<boolean>(false);
  private readonly _currentSeek = new ReactivePropertyInstance<number>(0);
  private readonly _isPlaying = new ReactivePropertyInstance<boolean>(false);
  private readonly _isSeeking = new ReactivePropertyInstance<boolean>(false);
  private readonly _playbackRate =
    new ReactivePropertyInstance<AudioPlaybackRate>(1);
  private readonly _currentTourId = new ReactivePropertyInstance<string | null>(
    null
  );
  private readonly _client = this._playerMode.select((x) =>
    x === "AudioSynthesis" ? this.audioSynthesisClient : this.audioPlayerClient
  );

  public currentTrack = this._currentTrack.toReadOnlyReactiveProperty();
  public isPlaying = this._isPlaying.toReadOnlyReactiveProperty();
  public isSeeking = this._isSeeking.toReadOnlyReactiveProperty();
  public currentSeek = this._currentSeek.toReadOnlyReactiveProperty();
  public canNext = this._canNext.toReadOnlyReactiveProperty();
  public canPrev = this._canPrev.toReadOnlyReactiveProperty();
  public playerMode = this._playerMode.toReadOnlyReactiveProperty();
  public playbackRate = this._playbackRate.toReadOnlyReactiveProperty();

  public constructor(
    audioPlayerClient: IAudioPlayerClient,
    audioSynthesisClient: IAudioSpeechClient,
    localizedEntityRepository: LocalizedEntityRepository,
    applicationStateRepository: ApplicationStateRepository,
    activityLogModule: ActivityLogModule,
    languages: Language[],
    libraryScope: LibraryScope
  ) {
    this.audioPlayerClient = audioPlayerClient;
    this.audioSynthesisClient = audioSynthesisClient;
    this.localizedEntityRepository = localizedEntityRepository;
    this.applicationStateRepository = applicationStateRepository;
    this.activityLogModule = activityLogModule;
    this.languages = languages;
    this.libraryScope = libraryScope;

    // 言語が変更され、ローカライズされたエンティティに変更が加わった時、ライブラリをリセット
    localizedEntityRepository.entities.getInstance().subscribe((_) => {
      this.audioPlayerClient.reset();
      this.audioSynthesisClient.reset();
      this._currentTourId.setValue(null);
    });

    this.audioPlayerClient.currentTrack
      .getInstance()
      .subscribe((x) => this._currentTrack.setValue(x));
    this.audioPlayerClient.isPlaying
      .getInstance()
      .subscribe((x) => this._isPlaying.setValue(x));
    this.audioPlayerClient.isSeeking
      .getInstance()
      .subscribe((x) => this._isSeeking.setValue(x));
    this.audioPlayerClient.currentSeek
      .getInstance()
      .subscribe((x) => this._currentSeek.setValue(x));
    this.audioPlayerClient.playbackRate
      .getInstance()
      .subscribe((x) => this._playbackRate.setValue(x));
    this.audioSynthesisClient.currentTrack
      .getInstance()
      .subscribe((x) => this._currentTrack.setValue(x));
    this.audioSynthesisClient.isPlaying
      .getInstance()
      .subscribe((x) => this._isPlaying.setValue(x));
    this.audioSynthesisClient.currentSeek
      .getInstance()
      .subscribe((x) => this._currentSeek.setValue(x));
  }

  public play = () => {
    if (!this._currentTrack.value) return;
    this._client.value.play();
    this.applicationStateRepository.addListenedGuideId(
      this._currentTrack.value.id
    );
  };

  public playOrPause = () => {
    this._isPlaying.value ? this._client.value.pause() : this.play();
  };

  public pause = () => this._client.value.pause();

  public seek = (to: number) => this._client.value.seek(to);

  public next = () => this.go(1);

  public prev = () => this.go(-1);

  public stop = () => this._client.value.reset();

  public select = (guideId: string) => {
    const entities = this.localizedEntityRepository.entities.value;
    const guide = entities.guides.find((x) => x.id === guideId);
    if (!guide) throw new Error(`Could not find a guide: ${guideId}`);

    // Tour が変更されている場合、プレイヤーの更新とライブラリの更新を行う
    if (guide.tourId !== this._currentTourId.value)
      this.updatePlayerFromGuide(guide);

    this.selectImpl(guideId);
  };

  public setPlaybackRate = (rate: AudioPlaybackRate) => {
    this._client.value.setPlaybackRate(rate);
  };

  private selectImpl = (trackId: string) => {
    this._client.value.selectTrack(trackId);
    this._canNext.setValue(this.checkCanGo(1));
    this._canPrev.setValue(this.checkCanGo(-1));
    this._currentSeek.setValue(0);
    this._isSeeking.setValue(false);

    this.activityLogModule.sendSelectGuideEvent(trackId);
  };

  /**
   * trackNumberDiff 分離れたインデックスのトラックが存在するか確認する
   * @param trackNumberDiff
   */
  private checkCanGo = (trackNumberDiff: number) => {
    const library = this.getValidLibrary();
    const nextIndex = this.getIndex(trackNumberDiff);
    return nextIndex >= 0 && library.length >= 0 && nextIndex < library.length;
  };

  /**
   * trackNumberDiff 分インデックスが離れたトラックを選択する
   * @private
   */
  private go = (trackNumberDiff: number) => {
    if (!this.checkCanGo(trackNumberDiff))
      throw new Error("Couldn't find next guide.");
    const library = this.getValidLibrary();
    const index = this.getIndex(trackNumberDiff);
    this.selectImpl(library[index].id);
    if (this._client.value.isPlaying.value) this.play();
  };

  /**
   * 選択したいガイドに見合ったプレイヤータイプへの変更と、ライブラリの更新を行う
   * @param guide
   */
  private updatePlayerFromGuide = (guide: Guide) => {
    const entities = this.localizedEntityRepository.entities.value;
    const applicationName = entities.application.name;

    let guides: Guide[] = [];
    const tour = entities.tours.find((x) => x.id === guide.tourId);
    if (!tour) throw new Error(`Could not find a tour: ${guide.tourId}`);

    const area = entities.areas.find((x) => x.id === tour?.areaId);
    if (!area) throw new Error(`Could not find a area: ${tour.areaId}`);

    if (this.libraryScope == "Tour") {
      guides = entities.guides.filter((x) => x.tourId === tour.id);
    } else if (this.libraryScope == "Area") {
      const tours = entities.tours.filter((x) => x.areaId === area.id);
      guides = entities.guides.filter((x) =>
        tours.map((x) => x.id).includes(x.tourId)
      );
    } else {
      entities.areas.forEach((x) => {
        const tours = entities.tours.filter((y) => y.areaId === x.id);
        guides.push(
          ...entities.guides.filter((y) =>
            tours.map((z) => z.id).includes(y.tourId)
          )
        );
      });
    }

    if (guides.length === 0) throw new Error("Could not find a guides");

    this.audioSynthesisClient.reset();
    this.audioPlayerClient.reset();
    if (tour.useAutoSpeech) {
      this._playerMode.setValue("AudioSynthesis");
      this.audioSynthesisClient.setLibrary(
        guides
          .sort((a, b) => a.trackNumber - b.trackNumber)
          .map((x) => guideToSpeechTrack(x, applicationName, this.languages))
      );
    } else {
      this._playerMode.setValue("AudioPlayer");
      this.audioPlayerClient.setLibrary(
        guides
          .sort((a, b) => a.trackNumber - b.trackNumber)
          .map((x) => guideToPlayerTrack(x, applicationName))
      );
    }
  };

  /**
   * ライブラリを取得する
   * ライブラリがない場合、エラーをスロー
   * @private
   */
  private getValidLibrary = () => {
    if (!this._client.value.currentLibrary)
      throw new Error("Can't call getValidLibrary with no library set");
    return this._client.value.currentLibrary;
  };

  /**
   * CurrentTrack を取得する
   * CurrentTrack がない場合、エラーをスロー
   * @private
   */
  private getValidCurrentTrack = () => {
    if (!this._client.value.currentTrack.value)
      throw new Error("Can't call getValidCurrentTrack with no track set");
    return this._client.value.currentTrack.value;
  };

  /**
   * currentTrack から trackNumberDiff分 index が離れた track のインデックスを取得する
   * @param trackNumberDiff
   */
  private getIndex = (trackNumberDiff: number) => {
    const library = this.getValidLibrary();
    const currentTrack = this.getValidCurrentTrack();
    return library.findIndex((x) => x.id === currentTrack.id) + trackNumberDiff;
  };
}
